import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { getSelectorAttribute, stripeCustomerDropdownSelectors } from '@vy/app-selectors';
import {
  INTEGRATION_PROVIDERS,
  type NewCustomer,
  type UpdateCustomer,
  type StripeCustomerEntity,
  type StripeIntermediateMetadata,
} from '@vy/types';
import {
  isEmptyString,
  isNotEmptyArray,
  isNullOrUndefined,
} from '@vy/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type Stripe from 'stripe';
import { useDebounce } from '../../../hooks/use-debounce';
import { useStripeCustomers } from '../../../hooks/requests/use-stripe-customers';
import { findIntegrationByProviderInEntities } from '@vy/services';
import {
  mergeUniqueStripeCustomers,
  stripeCustomersToIntermediateData,
  removeStripeEntity,
  createStripeEntity,
  toStripeIntermediateMetadata,
} from './stripe-customers-utils';

const DEBOUNCE_DELAY = 800;

type StripeCustomersDropdownProps = {
  showDropdown: boolean;
  label?: string;
};

export function StripeCustomersDropdown({
  showDropdown,
  label = 'Stripe Payment Provider',
}: StripeCustomersDropdownProps) {
  const [searchInput, setSearchValue] = useState<string | undefined>();
  const [allCustomers, setAllCustomers] = useState<Stripe.Customer[]>([]);
  const [selectedStripe, setSelectedStripe] = useState<StripeIntermediateMetadata | null>(null);
  const debouncedSearchValue = useDebounce(searchInput, DEBOUNCE_DELAY);

  const { setValue, getValues } = useFormContext<NewCustomer | UpdateCustomer>();

  const { data: { customers } = {}, isFetching } = useStripeCustomers(debouncedSearchValue, {
    enabled: showDropdown,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (isNotEmptyArray(customers)) {
      setAllCustomers((prevCustomers) => mergeUniqueStripeCustomers(prevCustomers, customers));
    }
  }, [customers]);

  const customersStripeData = useMemo(() => stripeCustomersToIntermediateData(allCustomers), [allCustomers]);

  const integrationEntities = getValues('integrationEntities') ?? [];
  const stripeEntity = findIntegrationByProviderInEntities<StripeCustomerEntity>(integrationEntities, INTEGRATION_PROVIDERS.stripe);
  const currentStripeEntity = toStripeIntermediateMetadata(stripeEntity);

  useEffect(() => {
    if (currentStripeEntity && !selectedStripe) {
      setSelectedStripe(currentStripeEntity);
    }
  }, [currentStripeEntity, selectedStripe]);

  const updateStripeEntity = (stripeMetadata: StripeIntermediateMetadata | null) => {
    setSelectedStripe(stripeMetadata);
    const currentEntities = getValues('integrationEntities') ?? [];
    const filteredEntities = removeStripeEntity(currentEntities);
    
    if (isNullOrUndefined(stripeMetadata)) {
      setValue('integrationEntities', filteredEntities, { shouldDirty: true });
      return;
    }
    
    const newEntity = createStripeEntity(stripeMetadata);
    setValue('integrationEntities', [...filteredEntities, newEntity], { shouldDirty: true });
  };

  const handleCustomerChange = (_: React.SyntheticEvent, value: StripeIntermediateMetadata | null) => {
    if (isNullOrUndefined(value)) {
      updateStripeEntity(null);
    } else {
      updateStripeEntity(value);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmptyString(e.target.value)) {
      updateStripeEntity(null);
    }
    setSearchValue(e.target.value);
  };

  return (
    <Autocomplete
      {...getSelectorAttribute(stripeCustomerDropdownSelectors.root)}
      disabled={!showDropdown}
      options={customersStripeData}
      getOptionLabel={(option) => option?.stripeName ?? ''}
      getOptionDisabled={(option) => option.stripeId === selectedStripe?.stripeId}
      onChange={handleCustomerChange}
      value={selectedStripe}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onChange={handleSearchChange}
          slotProps={{
            ...params.slotProps,

            input: {
              ...params.slotProps.input,
              endAdornment: (
                <React.Fragment>
                  {isFetching && <CircularProgress color="inherit" size={20} />}
                  {params.slotProps.input.endAdornment}
                </React.Fragment>
              ),
            },

            inputLabel: {
              shrink: true,
            }
          }} />
      )}
    />
  );
} 